import { Fragment, forwardRef, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, Menu, Popover, Transition } from "@headlessui/react";
import DOMPurify from "dompurify";
import {
  ArrowLeftIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  DotsHorizontalIcon,
  CreditCardIcon,
  InformationCircleIcon,
  KeyIcon,
  LinkIcon,
  LocationMarkerIcon,
  MailIcon,
  PaperClipIcon,
  PencilIcon,
  PlayIcon,
  ReceiptTaxIcon,
  ShareIcon,
  UserAddIcon,
  UserGroupIcon,
  XIcon,
} from "@heroicons/react/outline";
import JobComments from "./JobComments";
import CustomerFeedback from "./CustomerFeedback";
import JobActivityDrawer from "./JobActivityDrawer";
import { OverrideMark, useJobPricingEditor } from "./JobPricingEditor";
import {
  CardEditButton,
  FollowersEditDrawer,
  InvoiceEditDrawer,
  JobInformationEditDrawer,
  JobTagsEditor,
  LaborEditDrawer,
  OperationalEditDrawer,
} from "./JobInlineEditDrawers";

const STATUS = {
  U: { label: "Submitted", tone: "indigo" },
  A: { label: "Received", tone: "blue" },
  S: { label: "Assigned", tone: "amber" },
  W: { label: "In progress", tone: "emerald" },
  R: { label: "Review", tone: "purple" },
  C: { label: "Complete", tone: "emerald" },
  I: { label: "Invoiced", tone: "blue" },
  N: { label: "Not Invoiced", tone: "slate" },
  T: { label: "Canceled", tone: "slate" },
};

const STATUS_STYLES = {
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  purple: "bg-purple-50 text-purple-700 ring-purple-200",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
};

const CATEGORY = {
  I: "Interior",
  E: "Exterior",
  A: "Add-ons",
};

const FEE_LABEL = {
  A: "Travel fee",
  F: "FBO fee",
  G: "General fee",
  V: "Vendor price difference",
  M: "Management fee",
};

const DISCOUNT_LABEL = {
  S: "Service discount",
  A: "Airport discount",
  G: "General discount",
};

const RETAINER_COPY = "Covered under Monthly Retainer Agreement";
const TBD_COPY =
  "This service requires a quick review of the aircraft’s condition before pricing can be confirmed. The price will be updated soon";
const LINK_CLASS =
  "text-[#0284C7] transition hover:text-[#0369A1] hover:underline";
const DARK_LINK_CLASS =
  "text-[#0369A1] transition hover:text-[#075985] hover:underline";
const BRAND_ACTION_CLASS = "text-[#E3222A] transition hover:text-[#c91d24]";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatFlightTime(value) {
  if (!value) return "Not specified";
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  const hour = Number(match[1]);
  return `${hour % 12 || 12}:${match[2]} ${hour >= 12 ? "PM" : "AM"}`;
}

function formatFlightDate(value, timezone) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timezone || "UTC",
    }).format(new Date(value));
  } catch (error) {
    return "";
  }
}

function PlaneIcon({ className = "", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </svg>
  );
}

function getKeyLocationContent(tailDetails) {
  const note = String(tailDetails?.notes || "").trim();
  const [location, ...detailLines] = note.split(/\r?\n/).filter(Boolean);

  return {
    location: location || "Location details available",
    details: detailLines.join("\n"),
  };
}

function getKeyLocationAttachmentUrl(attachment) {
  const file = String(attachment?.file || "");
  if (!file) return "";

  return /^https?:\/\//i.test(file)
    ? file
    : `https://res.cloudinary.com/datidxeqm/${file}`;
}

function DetailsDrawer({
  open,
  onClose,
  badge,
  title,
  description,
  children,
  footer,
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[11000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[1px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-200"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto flex h-full w-[92vw] max-w-[480px] flex-col border-l border-slate-200 bg-white text-sm shadow-xl">
                  <div className="relative border-b border-slate-200 px-5 py-5">
                    <span className="inline-flex h-5 items-center rounded-full border border-sky-200 bg-sky-50 px-2 text-xs font-medium text-sky-700">
                      {badge}
                    </span>
                    <Dialog.Title className="mt-2 text-lg font-extrabold text-slate-950">
                      {title}
                    </Dialog.Title>
                    <Dialog.Description className="mt-0.5 text-sm text-slate-500">
                      {description}
                    </Dialog.Description>
                    <button
                      type="button"
                      onClick={onClose}
                      className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                    >
                      <XIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Close</span>
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-5">
                    {children}
                  </div>

                  <div className="mt-auto border-t border-slate-200 p-4">
                    {footer}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function KeyLocationDrawer({ open, onClose, tailDetails }) {
  const keyLocation = getKeyLocationContent(tailDetails);
  const attachments = tailDetails?.files || [];

  return (
    <DetailsDrawer
      open={open}
      onClose={onClose}
      badge="Visible to all job users"
      title="Key location"
      description="Where the aircraft key can be found"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-full items-center justify-center rounded-lg bg-[#E3222A] px-2.5 text-sm font-medium text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-[#E3222A] focus:ring-offset-2"
        >
          Done
        </button>
      }
    >
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-700">
          Current key location
        </p>
        <p className="mt-1 whitespace-pre-line text-base font-extrabold text-amber-950">
          {keyLocation.location}
        </p>
        {keyLocation.details && (
          <p className="mt-2 whitespace-pre-line text-sm leading-6 text-amber-900">
            {keyLocation.details}
          </p>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
            {attachments.length === 1 ? "Attachment" : "Attachments"}
          </p>
          <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {attachments.map((attachment) => {
              const attachmentUrl = getKeyLocationAttachmentUrl(attachment);

              return (
                <li
                  key={attachment.id || attachment.file}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <PaperClipIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                    {attachment.name || "Key location attachment"}
                  </span>
                  {attachmentUrl && (
                    <a
                      href={attachmentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={classNames(LINK_CLASS, "shrink-0 text-sm font-semibold")}
                    >
                      View
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </DetailsDrawer>
  );
}

function DrawerField({
  drawerType,
  label,
  name,
  value,
  onChange,
  readOnly,
  multiline = false,
}) {
  const id = `${drawerType}-${name}`;
  const sharedClassName = classNames(
    "mt-2 block w-full rounded-lg border border-slate-300 px-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200",
    multiline ? "min-h-28 py-2" : "h-8 py-1",
    readOnly && "cursor-not-allowed bg-slate-50 text-slate-600",
  );

  return (
    <label htmlFor={id} className="block">
      <span className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
        {label}
      </span>
      {multiline ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={sharedClassName}
        />
      ) : (
        <input
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          className={sharedClassName}
        />
      )}
    </label>
  );
}

function RecordDetailsDrawer({
  type,
  open,
  onClose,
  record,
  canEdit,
  saving,
  onSave,
}) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    if (type === "airport") {
      setForm({
        is_wet_wash_allowed: Boolean(record?.is_wet_wash_allowed),
        special_notes: record?.special_notes || "",
      });
    } else if (type === "fbo") {
      setForm({
        hours_of_operation: record?.hours_of_operation || "",
        phone_number: record?.phone_number || "",
        website: record?.website || "",
        special_notes: record?.special_notes || "",
      });
    } else {
      setForm({
        billing_address: record?.billing_address || "",
        phone_numbers: record?.phone_numbers || "",
        emails: record?.emails || "",
        notes: record?.notes || "",
      });
    }
  }, [open, record, type]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const handleSave = async () => {
    const saved = await onSave(form);
    if (saved !== false) onClose();
  };
  const title =
    type === "airport"
      ? `${record?.initials ? `${record.initials} · ` : ""}${record?.name || "Airport"}`
      : record?.name || (type === "fbo" ? "FBO" : "Assigned project manager");
  const description =
    type === "airport"
      ? "Airport operating details"
      : type === "fbo"
        ? "FBO access and operating details"
        : "Assigned project manager details";

  return (
    <DetailsDrawer
      open={open}
      onClose={onClose}
      badge={canEdit ? "Editable · Admin / Internal Coordinator" : "View only"}
      title={title}
      description={description}
      footer={
        canEdit ? (
          <div className="grid w-full grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex h-8 items-center justify-center rounded-lg bg-[#E3222A] px-2.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-full items-center justify-center rounded-lg bg-[#E3222A] px-2.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Done
          </button>
        )
      }
    >
      <div className="space-y-5">
        {type === "airport" && (
          <>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
              <div>
                <p className="text-sm font-bold text-slate-800">
                  Wet wash allowed
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Used for job validations and warnings.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(form.is_wet_wash_allowed)}
                disabled={!canEdit}
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    is_wet_wash_allowed: !current.is_wet_wash_allowed,
                  }))
                }
                className={classNames(
                  "relative inline-flex h-[18px] w-8 flex-none rounded-full transition disabled:cursor-not-allowed disabled:opacity-60",
                  form.is_wet_wash_allowed ? "bg-[#E3222A]" : "bg-slate-300",
                )}
              >
                <span
                  className={classNames(
                    "block h-4 w-4 rounded-full bg-white shadow transition",
                    form.is_wet_wash_allowed
                      ? "translate-x-[14px]"
                      : "translate-x-0.5",
                  )}
                />
                <span className="sr-only">Wet wash allowed</span>
              </button>
            </div>
            <DrawerField
              drawerType={type}
              label="Special notes"
              name="special_notes"
              value={form.special_notes || ""}
              onChange={handleChange}
              readOnly={!canEdit}
              multiline
            />
          </>
        )}
        {type === "fbo" && (
          <>
            <DrawerField
              drawerType={type}
              label="Hours of operation"
              name="hours_of_operation"
              value={form.hours_of_operation || ""}
              onChange={handleChange}
              readOnly={!canEdit}
            />
            <DrawerField
              drawerType={type}
              label="Phone number"
              name="phone_number"
              value={form.phone_number || ""}
              onChange={handleChange}
              readOnly={!canEdit}
            />
            <DrawerField
              drawerType={type}
              label="Website"
              name="website"
              value={form.website || ""}
              onChange={handleChange}
              readOnly={!canEdit}
            />
            <DrawerField
              drawerType={type}
              label="Special notes"
              name="special_notes"
              value={form.special_notes || ""}
              onChange={handleChange}
              readOnly={!canEdit}
              multiline
            />
          </>
        )}
        {type === "vendor" && (
          <>
            <DrawerField
              drawerType={type}
              label="Billing address"
              name="billing_address"
              value={form.billing_address || ""}
              onChange={handleChange}
              readOnly={!canEdit}
              multiline
            />
            <DrawerField
              drawerType={type}
              label="Phone numbers"
              name="phone_numbers"
              value={form.phone_numbers || ""}
              onChange={handleChange}
              readOnly={!canEdit}
            />
            <DrawerField
              drawerType={type}
              label="Emails"
              name="emails"
              value={form.emails || ""}
              onChange={handleChange}
              readOnly={!canEdit}
            />
            <DrawerField
              drawerType={type}
              label="Notes"
              name="notes"
              value={form.notes || ""}
              onChange={handleChange}
              readOnly={!canEdit}
              multiline
            />
          </>
        )}
        <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-500">
          Changes update the shared operational record.
        </p>
      </div>
    </DetailsDrawer>
  );
}

function Card({
  title,
  subtitle,
  icon: Icon,
  action,
  children,
  className = "",
  iconClassName = "",
  iconWrapperClassName = "",
  headerClassName = "",
  titleClassName = "",
}) {
  return (
    <section
      className={classNames(
        "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm",
        className,
      )}
    >
      {(title || action) && (
        <div
          className={classNames(
            "flex min-h-[52px] items-center justify-between gap-4 border-b border-slate-200 px-5 py-3",
            headerClassName,
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {Icon && (
              <span
                className={classNames(
                  "flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-red-50 text-[#E3222A]",
                  iconWrapperClassName,
                )}
              >
                <Icon
                  className={classNames("h-5 w-5", iconClassName)}
                  aria-hidden="true"
                />
              </span>
            )}
            <div className="min-w-0">
              <h2 className={classNames("text-sm font-extrabold text-slate-950", titleClassName)}>{title}</h2>
              {subtitle && (
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function JobInformationRow({ label, children, action }) {
  return (
    <div className="grid grid-cols-[94px_minmax(0,1fr)] gap-3 py-3 text-sm">
      <dt className="text-slate-400">{label}</dt>
      <dd className="flex min-w-0 items-start justify-between gap-3 font-semibold text-slate-700">
        <span className="min-w-0">{children || "Not specified"}</span>
        {action}
      </dd>
    </div>
  );
}

function InfoRow({ label, children, action }) {
  return (
    <div className="grid grid-cols-[118px_minmax(0,1fr)] gap-3 border-b border-slate-100 px-5 py-3 last:border-b-0">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="flex min-w-0 items-start justify-between gap-3 text-sm font-medium text-slate-900">
        <span className="min-w-0">{children || "Not specified"}</span>
        {action}
      </dd>
    </div>
  );
}

const SmallButton = forwardRef(function SmallButton(
  { children, className = "", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={classNames(
        "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={classNames(
        "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-transparent bg-[#E3222A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#c91d24] focus:outline-none focus:ring-2 focus:ring-[#E3222A] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function Price({ value, servicePrice = false }) {
  const numberValue = Number(
    typeof value === "string" ? value.replaceAll(",", "") : value,
  );
  const isMissing = value === null || value === undefined || !Number.isFinite(numberValue);
  const isTbd = servicePrice && (isMissing || numberValue === 0);

  if (isMissing && !servicePrice) {
    return <span className="text-slate-400">—</span>;
  }

  if (isTbd) {
    return (
      <Popover className="relative inline-flex">
        <Popover.Button className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200 focus:outline-none focus:ring-2 focus:ring-red-500">
          TBD
          <InformationCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </Popover.Button>
        <Popover.Panel className="absolute right-0 top-8 z-30 w-72 rounded-lg bg-slate-900 p-3 text-left text-xs font-normal leading-5 text-white shadow-xl">
          {TBD_COPY}
        </Popover.Panel>
      </Popover>
    );
  }

  return (
    <span>
      $
      {numberValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  );
}

function FeeValue({ fee, showPercentage = true }) {
  if (!fee) return <span className="text-slate-400">—</span>;
  const amount = fee.additional_fee_dollar_amount ?? fee.fee;
  const percentage = fee.hasAmountOverride ? fee.effectivePercentage : fee.isPercentage ? fee.fee : null;
  return (
    <span>
      {showPercentage && percentage != null && (
        <span className="mr-1 text-xs text-slate-500">{Number(Number(percentage).toFixed(2))}%</span>
      )}
      <Price value={amount} />
    </span>
  );
}

function sharedFeePercentage(customerFee, vendorFee) {
  const fees = [customerFee, vendorFee].filter(Boolean);
  if (
    fees.length === 0 ||
    fees.some((fee) => !fee.isPercentage) ||
    !fees.every((fee) => Number(fee.fee) === Number(fees[0].fee))
  ) {
    return null;
  }

  return fees[0].fee;
}

function sumFees(fees) {
  return fees.reduce((total, fee) => {
    const amount =
      fee.additional_fee_dollar_amount ?? (!fee.isPercentage ? fee.fee : 0);
    const numericAmount = Number(amount);
    return total + (Number.isFinite(numericAmount) ? numericAmount : 0);
  }, 0);
}

function sumDiscounts(discounts) {
  return discounts.reduce((total, discount) => {
    const amount =
      discount.discount_dollar_amount ??
      (!discount.isPercentage ? discount.discount : 0);
    const numericAmount = Number(amount);
    return total + (Number.isFinite(numericAmount) ? numericAmount : 0);
  }, 0);
}

function DiscountValue({ discount }) {
  if (!discount) return <span className="text-slate-400">—</span>;
  const amount = discount.discount_dollar_amount ?? discount.discount;
  const isZero = Number(amount) === 0;
  const percentage = discount.hasAmountOverride ? discount.effectivePercentage : discount.isPercentage ? discount.discount : null;
  return (
    <span>
      {percentage != null && (
        <span className="mr-1 text-xs text-slate-500">
          {Number(Number(percentage).toFixed(2))}%
        </span>
      )}
      {!isZero && "−"}
      <Price value={amount} />
    </span>
  );
}

function Scope({ service, modified = false }) {
  const [open, setOpen] = useState(false);
  const description = service.scope ?? (service.description || service.short_description || "");
  if (!description) return modified ? <p className="mt-2 text-xs text-slate-500">Scope cleared for this job.</p> : null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={classNames(
          "inline-flex items-center gap-1 text-xs font-bold",
          DARK_LINK_CLASS,
        )}
      >
        {open ? "Hide scope" : "View scope"}
        <OverrideMark active={modified} />
        <ChevronRightIcon
          className={classNames("h-3.5 w-3.5 transition", open && "rotate-90")}
        />
      </button>
      {open && (
        <div className="mt-2 max-w-2xl rounded-lg border border-sky-100 bg-sky-50 px-4 py-3">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-sky-700">
            SCOPE OF WORK
          </p>
          <div
            className="mt-2 space-y-2 text-sm font-normal leading-6 text-slate-700"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                description.replace(
                  /\r\n|\n/g,
                  "<br />",
                ),
              ),
            }}
          />
        </div>
      )}
    </div>
  );
}

export function ServicesCard({
  job,
  user,
  priceBreakdown,
  comparison,
  onAddService,
  onAddRetainerService,
  onRemoveService,
  onRemoveRetainerService,
  onCompleteService,
  onCompleteRetainerService,
  onSavePricingRow,
}) {
  const regularServices = useMemo(
    () => job.service_assignments || [],
    [job.service_assignments],
  );
  const retainerServices = useMemo(
    () => job.retainer_service_assignments || [],
    [job.retainer_service_assignments],
  );
  const isPureRetainer =
    job.service_catalog === "retainer" ||
    (regularServices.length === 0 && retainerServices.length > 0);
  const hasRetainer = retainerServices.length > 0;
  const isPrivilegedInternal =
    user.isAdmin ||
    user.isSuperUser ||
    user.isAccountManager ||
    user.isInternalCoordinator;
  const isVendorUser = !isPrivilegedInternal && Boolean(
    user.isProjectManager || user.isExternalProjectManager || user.isMasterPM,
  );
  const manuallySet = Boolean(
    priceBreakdown?.manuallySet || job.is_auto_priced === false,
  );
  const canSeePricingAdjustments = !isVendorUser;
  const canComparePrices =
    canSeePricingAdjustments &&
    user.canSeePrice &&
    !manuallySet &&
    isPrivilegedInternal &&
    comparison &&
    Object.keys(comparison).length > 0;
  const canSeeJobPrice =
    user.canSeePrice &&
    !isPureRetainer &&
    (!user.isExternalProjectManager ||
      (job.status !== "C" && job.status !== "I"));
  const canSeeSinglePrice = (canSeeJobPrice || (isPureRetainer && canComparePrices)) && !manuallySet;
  const showManualPrice = canSeeJobPrice && manuallySet;
  const canEditServices =
    user.isCustomer && (job.status === "A" || job.status === "U");
  const customerPricing = canComparePrices
    ? comparison.customerPriceBreakdown
    : !isVendorUser
      ? priceBreakdown
      : null;
  const vendorPricing = canComparePrices
    ? comparison.vendorPriceBreakdown
    : isVendorUser
      ? priceBreakdown
      : null;
  const showCustomerColumn = Boolean(canSeeSinglePrice && customerPricing);
  const showVendorColumn = Boolean(canSeeSinglePrice && vendorPricing);
  const showPricing = showCustomerColumn || showVendorColumn;
  const pricingEditor = useJobPricingEditor({
    showPriceOverrides: Boolean(isPrivilegedInternal),
    enabled: Boolean(canComparePrices && (!hasRetainer || isPureRetainer) && comparison?.canEditPricing && onSavePricingRow),
    fixedFeesOnly: isPureRetainer,
    revision: comparison?.pricingRevision,
    onSave: onSavePricingRow,
    adjustmentTypes: comparison?.adjustmentTypes,
  });
  const customerPriceLabel = user.isCustomer ? "Price" : "Customer Price";
  const vendorPriceLabel = isVendorUser ? "Price" : "Vendor Price";
  const serviceGridClass =
    showCustomerColumn && showVendorColumn
      ? "sm:grid-cols-[minmax(0,1fr)_110px_110px_auto]"
      : showPricing
        ? "sm:grid-cols-[minmax(0,1fr)_110px_auto]"
        : "sm:grid-cols-[minmax(0,1fr)_auto]";

  const customerPriceById = useMemo(
    () =>
      Object.fromEntries(
        (customerPricing?.services || []).map((service) => [
          String(service.id),
          service,
        ]),
      ),
    [customerPricing],
  );
  const vendorPriceById = useMemo(
    () =>
      Object.fromEntries(
        (vendorPricing?.services || []).map((service) => [
          String(service.id),
          service,
        ]),
      ),
    [vendorPricing],
  );

  const groupedServices = useMemo(() => {
    const groups = [];
    [
      ...regularServices,
      ...retainerServices.map((service) => ({ ...service, isRetainer: true })),
    ].forEach((service) => {
      const category = service.category || "O";
      let group = groups.find((item) => item.key === category);
      if (!group) {
        group = {
          key: category,
          label: CATEGORY[category] || "Other",
          services: [],
        };
        groups.push(group);
      }
      group.services.push(service);
    });
    return groups.sort(
      (a, b) =>
        ["I", "E", "A", "O"].indexOf(a.key) -
        ["I", "E", "A", "O"].indexOf(b.key),
    );
  }, [regularServices, retainerServices]);

  const customerFees = useMemo(
    () => (customerPricing?.additionalFees || []).filter((fee) => !isPureRetainer || !fee.isPercentage),
    [customerPricing, isPureRetainer],
  );
  const vendorFees = useMemo(
    () => (vendorPricing?.additionalFees || []).filter((fee) => !isPureRetainer || !fee.isPercentage),
    [vendorPricing, isPureRetainer],
  );
  const feeRows = customerFees.length ? customerFees : vendorFees;
  const customerDiscounts = useMemo(
    () => isPureRetainer ? [] : customerPricing?.discounts || [],
    [customerPricing, isPureRetainer],
  );
  const vendorDiscounts = useMemo(
    () => isPureRetainer ? [] : vendorPricing?.discounts || [],
    [vendorPricing, isPureRetainer],
  );
  const discountRows = customerDiscounts.length
    ? customerDiscounts
    : vendorDiscounts;
  const comparisonFeeRows = useMemo(() => {
    const rows = customerFees.map((customerFee, index) => ({
      key: `${customerFee.name}-${customerFee.id ?? index}`,
      label: customerFee.name,
      customer: customerFee,
      vendor:
        vendorFees.find((item) => item.id === customerFee.id) ||
        (customerFee.defaultAmount === undefined ? vendorFees.find((item) => item.name === customerFee.name) || vendorFees[index] : null),
    }));
    vendorFees.forEach((vendorFee, index) => {
      if (!rows.some((row) => row.vendor === vendorFee)) {
        rows.push({
          key: `vendor-${vendorFee.name}-${vendorFee.id ?? index}`,
          label: vendorFee.name,
          customer: null,
          vendor: vendorFee,
        });
      }
    });
    return rows;
  }, [customerFees, vendorFees]);
  const comparisonDiscountRows = useMemo(() => {
    const rows = customerDiscounts.map((customerDiscount, index) => ({
      key: `${customerDiscount.name}-${customerDiscount.id ?? index}`,
      label: customerDiscount.name,
      customer: customerDiscount,
      vendor:
        vendorDiscounts.find((item) => item.id === customerDiscount.id) ||
        (customerDiscount.defaultAmount === undefined ? vendorDiscounts.find((item) => item.name === customerDiscount.name) || vendorDiscounts[index] : null),
    }));
    vendorDiscounts.forEach((vendorDiscount, index) => {
      if (!rows.some((row) => row.vendor === vendorDiscount)) {
        rows.push({
          key: `vendor-${vendorDiscount.name}-${vendorDiscount.id ?? index}`,
          label: vendorDiscount.name,
          customer: null,
          vendor: vendorDiscount,
        });
      }
    });
    return rows;
  }, [customerDiscounts, vendorDiscounts]);
  const singleTotal = customerPricing?.totalPrice ?? vendorPricing?.totalPrice;
  const addFeeButton = pricingEditor.addButton("fee");
  const addDiscountButton = pricingEditor.addButton("discount");

  return (
    <Card
      title="Services"
      subtitle={
        isPureRetainer ? "Monthly Retainer Services" : "Regular Services"
      }
      icon={BriefcaseIcon}
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {hasRetainer && (
            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
              Monthly retainer
            </span>
          )}
          {canEditServices && (
            <>
              {!isPureRetainer && (
                <SmallButton onClick={onAddService}>Add service</SmallButton>
              )}
              {isPureRetainer && user.isPremiumMember && (
                <SmallButton onClick={onAddRetainerService}>
                  Add retainer service
                </SmallButton>
              )}
            </>
          )}
        </div>
      }
    >
      {isPureRetainer && (
        <div className="flex items-start gap-3 border-b border-violet-100 bg-violet-50 px-5 py-4">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-violet-600" />
          <div>
            <p className="text-sm font-semibold text-violet-900">
              Retainer status
            </p>
            <p className="mt-0.5 text-sm text-violet-800">{RETAINER_COPY}</p>
          </div>
        </div>
      )}

      {groupedServices.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-slate-500">
          No services found.
        </div>
      ) : (
        <div
          className={classNames(
            "grid",
            showPricing && canComparePrices
              ? "xl:grid-cols-[minmax(0,1fr)_310px]"
              : "grid-cols-1",
          )}
        >
          <div
            className={classNames(
              showPricing &&
                canComparePrices &&
                "xl:border-r xl:border-slate-200",
            )}
          >
            {showPricing && (
              <div
                className={classNames(
                  "hidden gap-3 bg-slate-50 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 sm:grid",
                  serviceGridClass,
                )}
              >
                <span>Service</span>
                {showCustomerColumn && (
                  <span className="text-right">{customerPriceLabel}</span>
                )}
                {showVendorColumn && (
                  <span className="text-right">{vendorPriceLabel}</span>
                )}
                <span aria-hidden="true" />
              </div>
            )}
            {groupedServices.map((group) => (
              <div
                key={group.key}
                className="border-b border-slate-200 last:border-b-0"
              >
                <div className="bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {group.label}
                </div>
                <div className="divide-y divide-slate-100">
                  {group.services.map((service) => {
                    const customerPrice =
                      customerPriceById[String(service.service_id ?? service.id)] ||
                      (customerPricing?.services || []).find(
                        (item) => item.name === service.name,
                      );
                    const vendorPrice =
                      vendorPriceById[String(service.service_id ?? service.id)] ||
                      (vendorPricing?.services || []).find(
                        (item) => item.name === service.name,
                      );
                    const pricingRow = { kind: "service", id: customerPrice?.id ?? vendorPrice?.id, name: service.name, customer: customerPrice, vendor: vendorPrice, scope: service.scope ?? (service.description || service.short_description || "") };
                    return (
                      <div
                        key={`${service.isRetainer ? "r" : "s"}-${service.id}`}
                        className="px-5 py-4"
                      >
                        <div
                          className={classNames(
                            "grid gap-3 sm:items-start",
                            serviceGridClass,
                          )}
                        >
                          <div className="flex min-w-0 items-start gap-2.5">
                            {service.status === "C" && (
                              <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full bg-emerald-100 text-emerald-700">
                                <CheckIcon
                                  className="h-3.5 w-3.5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                            <div className={classNames("min-w-0", pricingEditor.isEditing(pricingRow) && "flex-1")}>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className={classNames("text-sm font-semibold text-slate-800", pricingEditor.isEditing(pricingRow) && "w-full min-w-0")}>
                                  {service.isRetainer ? service.name : pricingEditor.cell(pricingRow, "name", customerPrice?.displayName || vendorPrice?.displayName || service.name)}
                                </p>
                                {service.isRetainer && (
                                  <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
                                    Retainer
                                  </span>
                                )}
                              </div>
                              {service.isRetainer ? <Scope service={service} /> : pricingEditor.scope(pricingRow,
                                <Scope service={{ ...service, scope: customerPrice?.scope ?? vendorPrice?.scope ?? service.scope }} modified={customerPrice?.scopeOverridden || vendorPrice?.scopeOverridden} />
                              )}
                            </div>
                          </div>

                          {showCustomerColumn && (
                            <div className="mt-2 flex items-center justify-between text-sm sm:mt-0 sm:justify-end">
                              <span className="text-xs font-medium text-slate-400 sm:hidden">
                                {customerPriceLabel}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {service.isRetainer ? (
                                  "—"
                                ) : (
                                  pricingEditor.cell(pricingRow, "customer_amount", <Price value={customerPrice?.price} servicePrice />)
                                )}
                              </span>
                            </div>
                          )}
                          {showVendorColumn && (
                            <div className="mt-1 flex items-center justify-between text-sm sm:mt-0 sm:justify-end">
                              <span className="text-xs font-medium text-slate-400 sm:hidden">
                                {vendorPriceLabel}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {service.isRetainer ? (
                                  "—"
                                ) : (
                                  pricingEditor.cell(pricingRow, "vendor_amount", <Price value={vendorPrice?.price} servicePrice />)
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex items-start justify-end gap-2">
                            {!service.isRetainer && pricingEditor.actions(pricingRow)}
                            {canEditServices && (
                              <button
                                type="button"
                                onClick={() =>
                                  service.isRetainer
                                    ? onRemoveRetainerService(service)
                                    : onRemoveService(service)
                                }
                                className="text-xs font-medium text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        {!user.isCustomer && service.status === "W" && (
                          <div className="mt-1 flex justify-end">
                            <SmallButton
                              onClick={() =>
                                service.isRetainer
                                  ? onCompleteRetainerService(service.id)
                                  : onCompleteService(service.id)
                              }
                              className="!px-2 !py-0.5 text-xs"
                            >
                              Mark complete
                            </SmallButton>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {showManualPrice && !isVendorUser && (
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4 text-sm">
                <span className="font-semibold text-slate-700">
                  {user.isCustomer || isVendorUser ? "Price" : "Job Price"}
                </span>
                <span className="text-base font-bold text-slate-900">
                  <Price value={job.price} />
                </span>
              </div>
            )}

            {canSeePricingAdjustments &&
              showPricing &&
              canComparePrices &&
              (comparisonFeeRows.length > 0 || addFeeButton) && (
                <div className="border-b border-slate-200">
                  <div className="flex items-center justify-between gap-3 bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Additional Fees</span>
                    {addFeeButton}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {comparisonFeeRows.map((row) => {
                      const pricingRow = { kind: "fee", id: row.customer?.id ?? row.vendor?.id, name: row.customer?.displayName || row.vendor?.displayName || FEE_LABEL[row.label] || row.label, customer: row.customer, vendor: row.vendor };
                      const percentage = sharedFeePercentage(
                        row.customer,
                        row.vendor,
                      );
                      return (
                        <div
                          key={row.key}
                          className={classNames(
                            "grid gap-3 px-5 py-4 sm:items-center",
                            serviceGridClass,
                          )}
                        >
                          <div className="text-sm font-semibold text-slate-800">
                            {pricingEditor.cell(pricingRow, "name", pricingRow.name)}
                            {percentage !== null && (
                              <span className="ml-1 text-xs font-medium text-slate-500">
                                · {percentage}%
                              </span>
                            )}
                          </div>
                          {showCustomerColumn && (
                            <div className="flex items-center justify-between text-sm sm:justify-end">
                              <span className="text-xs font-medium text-slate-400 sm:hidden">
                                {customerPriceLabel}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {pricingEditor.cell(pricingRow, "customer_amount", <FeeValue
                                  fee={row.customer}
                                  showPercentage={percentage === null}
                                />)}
                              </span>
                            </div>
                          )}
                          {showVendorColumn && (
                            <div className="flex items-center justify-between text-sm sm:justify-end">
                              <span className="text-xs font-medium text-slate-400 sm:hidden">
                                {vendorPriceLabel}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {pricingEditor.cell(pricingRow, "vendor_amount", <FeeValue
                                  fee={row.vendor}
                                  showPercentage={percentage === null}
                                />)}
                              </span>
                            </div>
                          )}
                          {pricingEditor.actions(pricingRow) || <span aria-hidden="true" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {canSeePricingAdjustments &&
              showPricing &&
              canComparePrices &&
              (comparisonDiscountRows.length > 0 || addDiscountButton) && (
                <div className="border-b border-slate-200">
                  <div className="flex items-center justify-between gap-3 bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Discounts</span>
                    {addDiscountButton}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {comparisonDiscountRows.map((row) => {
                      const pricingRow = { kind: "discount", id: row.customer?.id ?? row.vendor?.id, name: row.customer?.displayName || row.vendor?.displayName || DISCOUNT_LABEL[row.label] || row.label, customer: row.customer, vendor: row.vendor };
                      return (
                      <div
                        key={row.key}
                        className={classNames(
                          "grid gap-3 px-5 py-4 sm:items-center",
                          serviceGridClass,
                        )}
                      >
                        <div className="text-sm font-semibold text-slate-800">
                          {pricingEditor.cell(pricingRow, "name", pricingRow.name)}
                        </div>
                        {showCustomerColumn && (
                          <div className="flex items-center justify-between text-sm sm:justify-end">
                            <span className="text-xs font-medium text-slate-400 sm:hidden">
                              {customerPriceLabel}
                            </span>
                            <span className="font-semibold text-slate-800">
                              {pricingEditor.cell(pricingRow, "customer_amount", <DiscountValue discount={row.customer} />)}
                            </span>
                          </div>
                        )}
                        {showVendorColumn && (
                          <div className="flex items-center justify-between text-sm sm:justify-end">
                            <span className="text-xs font-medium text-slate-400 sm:hidden">
                              {vendorPriceLabel}
                            </span>
                            <span className="font-semibold text-slate-800">
                              {pricingEditor.cell(pricingRow, "vendor_amount", <DiscountValue discount={row.vendor} />)}
                            </span>
                          </div>
                        )}
                        {pricingEditor.actions(pricingRow) || <span aria-hidden="true" />}
                      </div>
                    );})}
                  </div>
                </div>
              )}

            {!isPureRetainer && showPricing && !canComparePrices && !isVendorUser && (
              <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
                {canSeePricingAdjustments && discountRows.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Discounts
                    </h3>
                    <div className="mt-2 space-y-2">
                      {discountRows.map((discount, index) => (
                        <div
                          key={discount.id ?? `${discount.name}-${index}`}
                          className="flex justify-between gap-4 text-sm"
                        >
                          <span className="text-slate-600">
                            {discount.displayName || DISCOUNT_LABEL[discount.name] || discount.name}
                          </span>
                          <span className="font-medium text-slate-900">
                            <DiscountValue discount={discount} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {canSeePricingAdjustments && feeRows.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Additional fees
                    </h3>
                    <div className="mt-2 space-y-2">
                      {feeRows.map((fee, index) => (
                        <div
                          key={fee.id ?? `${fee.name}-${index}`}
                          className="flex justify-between gap-4 text-sm"
                        >
                          <span className="text-slate-600">
                            {fee.displayName || FEE_LABEL[fee.name] || fee.name}
                          </span>
                          <span className="font-medium text-slate-900">
                            <FeeValue fee={fee} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
                  <span className="font-semibold text-slate-700">Total</span>
                  <span className="text-base font-bold text-slate-900">
                    <Price value={singleTotal} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {showPricing && canComparePrices && (
            <aside className="bg-slate-50 p-5">
              <h3 className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                FINANCIAL SUMMARY
              </h3>
              <dl className="mt-5 border-t border-slate-300 pt-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Customer Subtotal</dt>
                  <dd className="font-medium text-slate-800">
                    <Price value={customerPricing?.servicesPrice} />
                  </dd>
                </div>
                <div className="mt-3 flex justify-between gap-4">
                  <dt className="text-slate-500">Customer Fees</dt>
                  <dd className="font-medium text-slate-800">
                    <Price value={sumFees(customerFees)} />
                  </dd>
                </div>
                {customerDiscounts.length > 0 && (
                  <div className="mt-3 flex justify-between gap-4">
                    <dt className="text-slate-500">Customer Discounts</dt>
                    <dd className="font-medium text-slate-800">
                      {sumDiscounts(customerDiscounts) !== 0 && "−"}
                      <Price
                        value={sumDiscounts(customerDiscounts)}
                      />
                    </dd>
                  </div>
                )}
                <div className="mt-3 flex justify-between gap-4 border-t border-slate-300 pt-3">
                  <dt className="font-extrabold text-slate-950">
                    Projected Customer Total
                  </dt>
                  <dd className="text-base font-extrabold text-slate-950">
                    <Price value={customerPricing?.totalPrice} />
                  </dd>
                </div>

                <div className="mt-4 flex justify-between gap-4 border-t border-slate-300 pt-4">
                  <dt className="text-slate-500">Vendor Subtotal</dt>
                  <dd className="font-medium text-slate-800">
                    <Price value={vendorPricing?.servicesPrice} />
                  </dd>
                </div>
                <div className="mt-3 flex justify-between gap-4">
                  <dt className="text-slate-500">Vendor Fees</dt>
                  <dd className="font-medium text-slate-800">
                    <Price value={sumFees(vendorFees)} />
                  </dd>
                </div>
                {vendorDiscounts.length > 0 && (
                  <div className="mt-3 flex justify-between gap-4">
                    <dt className="text-slate-500">Vendor Discounts</dt>
                    <dd className="font-medium text-slate-800">
                      {sumDiscounts(vendorDiscounts) !== 0 && "−"}
                      <Price
                        value={sumDiscounts(vendorDiscounts)}
                      />
                    </dd>
                  </div>
                )}
                <div className="mt-3 flex justify-between gap-4 border-t border-slate-300 pt-3">
                  <dt className="font-extrabold text-slate-950">
                    Projected Vendor Total
                  </dt>
                  <dd className="text-base font-extrabold text-slate-950">
                    <Price value={vendorPricing?.totalPrice} />
                  </dd>
                </div>
              </dl>

              {(user.isAdmin ||
                user.isSuperUser ||
                user.isAccountManager ||
                (user.isInternalCoordinator && user.canSeePrice)) && (
                <div className="mt-5 border-t border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div className="flex items-center justify-between text-sm font-extrabold text-emerald-800">
                    <span>Projected Profit</span>
                    <span>
                      <Price value={comparison.projectedProfit} />
                    </span>
                  </div>
                  <p className="mt-1 text-right text-xs font-semibold text-emerald-700">
                    {comparison.projectedProfitPercentage}% margin
                  </p>
                </div>
              )}
            </aside>
          )}
        </div>
      )}
      {pricingEditor.feedback}
    </Card>
  );
}

function PriceVisibilityToggle({ projectManager, disabled, onChange }) {
  if (!projectManager) return null;

  const visible = Boolean(projectManager.show_price_breakdown);
  return (
    <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-4">
      <div>
        <p className="text-sm font-semibold text-slate-900">Can view prices</p>
        <p className="mt-0.5 text-xs text-slate-500">Pricing visibility</p>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onChange}
        aria-label="Can view prices"
        aria-pressed={visible}
        className={classNames(
          "relative inline-flex h-6 w-11 flex-none rounded-full border-2 border-transparent transition focus:outline-none focus:ring-2 focus:ring-[#E3222A] focus:ring-offset-2 disabled:opacity-50",
          visible ? "bg-[#E3222A]" : "bg-slate-300",
        )}
      >
        <span
          className={classNames(
            "inline-block h-5 w-5 rounded-full bg-white shadow transition",
            visible ? "translate-x-5" : "translate-x-0",
          )}
        />
      </button>
    </div>
  );
}

function OperationalCard({ job, onAirportClick, onFboClick, canEdit, onEdit }) {
  return (
    <Card
      title="Operational summary"
      icon={LocationMarkerIcon}
      action={canEdit && <CardEditButton label="Edit operational summary" onClick={onEdit} />}
    >
      <dl>
        <InfoRow label="AIRPORT">
          <button
            type="button"
            onClick={onAirportClick}
            className={classNames("text-left font-semibold", LINK_CLASS)}
          >
            {job.airport?.initials && (
              <span className="mr-1">{job.airport.initials} ·</span>
            )}
            {job.airport?.name}
            <LinkIcon className="ml-1 inline h-3 w-3" aria-hidden="true" />
          </button>
        </InfoRow>
        <InfoRow label="FBO">
          <button
            type="button"
            onClick={onFboClick}
            className={classNames("text-left font-semibold", LINK_CLASS)}
          >
            {job.fbo?.name}
            <LinkIcon className="ml-1 inline h-3 w-3" aria-hidden="true" />
          </button>
        </InfoRow>
        {job.fbo?.hours_of_operation && (
          <div className="mx-5 mt-3 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-blue-700">
            <ClockIcon className="mt-0.5 h-5 w-5 flex-none" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="text-sm font-semibold text-blue-800">Hours of operation</dt>
              <dd className="mt-1 whitespace-pre-wrap break-words text-sm leading-6">
                {job.fbo.hours_of_operation}
              </dd>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 px-5 py-4">
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Arrival
            </dt>
            <dd className="mt-1 text-sm font-bold text-slate-900">
              {job.on_site
                ? "On site"
                : job.arrival_formatted_date || "Not specified"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Departure
            </dt>
            <dd className="mt-1 text-sm font-bold text-slate-900">
              {job.departure_formatted_date || "Not specified"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Complete by
            </dt>
            <dd className="mt-1 text-sm font-bold text-slate-900">
              {job.completeBy ? (
                job.complete_before_formatted_date
              ) : (
                <span className="text-amber-700">TBD</span>
              )}
            </dd>
          </div>
        </div>
        {job.completion_date && (
          <InfoRow label="Completed">
            <span className="font-bold text-slate-900">{job.completion_date}</span>
          </InfoRow>
        )}
      </dl>
    </Card>
  );
}

function JobInformationCard({ job, user, isInternal, actions, canEdit, onEdit }) {
  return (
    <Card
      title="Job information"
      icon={PlaneIcon}
      iconWrapperClassName="bg-slate-100 text-[#E3222A]"
      headerClassName="py-4"
      action={canEdit && <CardEditButton label="Edit job information" onClick={onEdit} />}
    >
      <dl className="divide-y divide-slate-100 px-5">
        <JobInformationRow label="Aircraft">
          {job.aircraftType?.name}
        </JobInformationRow>
        <JobInformationRow label="Customer PO">
          {job.customer_purchase_order || "Not provided"}
        </JobInformationRow>
        {(isInternal || user.isCustomer) && (
          <JobInformationRow label="Requested by">
            {job.requested_by ||
              `${job.created_by?.first_name || ""} ${job.created_by?.last_name || ""}`.trim()}
          </JobInformationRow>
        )}
        {(isInternal || user.isCustomer) && job.is_publicly_confirmed && (
          <JobInformationRow label="Confirmed by">
            <span>
              {job.confirmed_full_name}
              {job.confirmed_email && (
                <span className="block text-xs font-normal text-slate-500">
                  {job.confirmed_email}
                </span>
              )}
              {job.confirmed_phone_number && (
                <span className="block text-xs font-normal text-slate-500">
                  {job.confirmed_phone_number}
                </span>
              )}
            </span>
          </JobInformationRow>
        )}
        <JobInformationRow label="Request date">
          {job.requestDate}
        </JobInformationRow>
        {(isInternal || user.isCustomer) && (
          <div className="py-3">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-slate-400">Categories</dt>
              <button
                type="button"
                onClick={actions.openCategories}
                className={classNames(
                  "text-xs font-semibold",
                  BRAND_ACTION_CLASS,
                )}
              >
                Manage
              </button>
            </div>
            <dd className="mt-2 font-semibold text-slate-700">
              {job.categories?.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {job.categories.map((category) => (
                    <span
                      key={category.id}
                      className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                    >
                      {category.customer_category.name}
                    </span>
                  ))}
                </span>
              ) : (
                "None"
              )}
            </dd>
          </div>
        )}
        {(isInternal || user.isCustomer) &&
          job.inspection?.initial_inspection_done_by && (
            <JobInformationRow
              label="Initial inspection"
              action={
                <button
                  type="button"
                  onClick={() => actions.openInspection("I")}
                  className={classNames(
                    "text-xs font-semibold",
                    BRAND_ACTION_CLASS,
                  )}
                >
                  Checklist
                </button>
              }
            >
              {job.inspection.initial_inspection_done_by}
            </JobInformationRow>
          )}
        {(isInternal || user.isCustomer) &&
          job.inspection?.final_inspection_done_by && (
            <JobInformationRow
              label="Final inspection"
              action={
                <button
                  type="button"
                  onClick={() => actions.openInspection("F")}
                  className={classNames(
                    "text-xs font-semibold",
                    BRAND_ACTION_CLASS,
                  )}
                >
                  Checklist
                </button>
              }
            >
              {job.inspection.final_inspection_done_by}
            </JobInformationRow>
          )}
      </dl>
    </Card>
  );
}

function InboundFlightCard({ job, flights }) {
  const [showFlights, setShowFlights] = useState(false);

  return (
    <Card
      title="Inbound flight"
      subtitle={`FlightAware · Call sign ${job.tailNumber}`}
      icon={PlaneIcon}
      iconWrapperClassName="bg-slate-100 text-[#0284C7]"
      headerClassName="py-4"
      action={
        <button
          type="button"
          onClick={() => setShowFlights((value) => !value)}
          aria-expanded={showFlights}
          aria-controls={`job-${job.id}-flights`}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-sky-700 transition hover:bg-slate-100"
        >
          {showFlights ? "Hide Flights" : "View Flights"}
          <ChevronDownIcon className={classNames("h-3.5 w-3.5 transition", showFlights && "rotate-180")} aria-hidden="true" />
        </button>
      }
    >
      {showFlights && (
        <ul id={`job-${job.id}-flights`} className="divide-y divide-slate-200">
          {flights.map((flight, index) => (
            <li key={flight.fa_flight_id || flight.id || index} className="px-5 py-4 sm:px-6">
              <div className="mb-3 flex justify-end text-xs font-semibold text-slate-600">
                {flight.status}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-base font-black text-slate-950">{flight.origin?.code || "—"}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                  <span className="block h-full rounded-full bg-sky-500" style={{ width: `${Math.min(100, Math.max(0, flight.progress_percent || 0))}%` }} />
                </div>
                <span className="text-base font-black text-slate-950">{flight.destination?.code || "—"}</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 sm:gap-6">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-400">Departure</p>
                  <p className="mt-1 font-semibold text-slate-800">{flight.origin?.city}</p>
                  <p className="break-words text-xs text-slate-500">{flight.origin?.timezone}</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div><p className="text-xs text-slate-500">Scheduled departure</p><p className="mt-1 font-extrabold text-slate-800">{formatFlightTime(flight.local_scheduled_off)}</p></div>
                    <div><p className="text-xs text-slate-500">Estimated takeoff</p><p className="mt-1 font-extrabold text-slate-800">{formatFlightTime(flight.local_estimated_off)}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-400">Arrival</p>
                  <p className="mt-1 font-semibold text-slate-800">{flight.destination?.city}</p>
                  <p className="break-words text-xs text-slate-500">{flight.destination?.timezone}</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Scheduled arrival</p>
                      <p className="mt-1 font-extrabold text-slate-800">{formatFlightTime(flight.local_scheduled_on)}</p>
                      <p className="text-xs text-slate-500">{formatFlightDate(flight.scheduled_on, flight.destination?.timezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Estimated landing</p>
                      <p className="mt-1 font-extrabold text-sky-700">{formatFlightTime(flight.local_estimated_on)}</p>
                      {flight.arrival_delay > 0 && <p className="text-xs font-semibold text-amber-700">{Math.max(1, Math.round(flight.arrival_delay / 60))} min late</p>}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AssignmentCard({
  jobId,
  job,
  vendor,
  comparison,
  actions,
  canManage,
  canEdit = canManage,
  onVendorClick,
  onEditLabor,
}) {
  const projectManager = comparison?.assigned_project_manager_info;
  const projectManagerName = [
    projectManager?.first_name,
    projectManager?.last_name,
  ]
    .filter(Boolean)
    .join(" ");
  const assigneeName = vendor?.name || projectManagerName || "Not assigned";

  return (
    <Card
      title="Assignment"
      icon={UserGroupIcon}
      action={
        canEdit && (
          <Link
            to={`/jobs/${jobId}/assignments`}
            className={classNames("text-xs font-semibold", BRAND_ACTION_CLASS)}
          >
            Manage
          </Link>
        )
      }
    >
      <button
        type="button"
        onClick={vendor?.id ? onVendorClick : undefined}
        disabled={!vendor?.id}
        className="flex w-full items-center gap-3 px-5 py-4 text-left disabled:cursor-default"
      >
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#E3222A] text-xs font-black text-white">
          {(assigneeName.match(/\b\w/g) || [])
            .slice(0, 2)
            .join("")
            .toUpperCase() || "—"}
        </span>
        <span className="min-w-0">
          <span
            className={classNames(
              "block truncate text-sm font-extrabold",
              vendor?.id ? DARK_LINK_CLASS : "text-slate-900",
            )}
          >
            {assigneeName}
            {vendor?.id && (
              <LinkIcon className="ml-1 inline h-3 w-3" aria-hidden="true" />
            )}
          </span>
          <span className="mt-0.5 block text-xs text-slate-500">
            Assigned project manager · one per job
          </span>
        </span>
      </button>
      {canManage && (
        <PriceVisibilityToggle
          projectManager={projectManager}
          disabled={actions.savingPmPriceVisibility}
          onChange={actions.togglePmPriceVisibility}
        />
      )}
      <dl className="grid grid-cols-2 border-t border-slate-100">
        <div className="border-r border-slate-100 px-5 py-4">
          <dt className="text-xs text-slate-500">Workers</dt>
          <dd className="mt-1 text-lg font-bold text-slate-950">
            {job.number_of_workers || 0}
          </dd>
        </div>
        <div className="relative px-5 py-4">
          <dt className="text-xs text-slate-500">Labor time</dt>
          {canEdit && (
            <button
              type="button"
              onClick={onEditLabor}
              aria-label="Edit labor time"
              className="absolute right-4 top-2.5 inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <dd className="mt-1 text-lg font-bold text-slate-950">
            {job.labor_time
              ? `${Number(job.labor_time).toFixed(1)}h`
              : `${job.hours_worked || 0}h ${job.minutes_worked || 0}m`}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

function AttachmentsCard({ job, user, isAdmin, actions }) {
  return (
    <Card
      title="Attachments"
      subtitle="Job documents"
      icon={PaperClipIcon}
      action={
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
          {job.files?.length || 0}
        </span>
      }
    >
      {!job.files?.length ? (
        <div className="px-5 py-6 text-sm text-slate-500">
          No file attachments found.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {job.files.map((file) => (
            <li key={file.id} className="px-5 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="rounded-lg bg-slate-100 p-2 text-slate-500">
                  <PaperClipIcon className="h-4 w-4" />
                </span>
                <button
                  type="button"
                  onClick={() => actions.downloadFile(file)}
                  className={classNames(
                    "min-w-0 flex-1 truncate text-left text-sm font-bold",
                    DARK_LINK_CLASS,
                  )}
                >
                  {file.name}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => actions.deleteFile(file)}
                    className="text-xs font-semibold text-[#E3222A] hover:text-[#c91d24]"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between pl-11 text-xs text-slate-500">
                <span>{file.created_at && `Uploaded ${file.created_at}`}</span>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => actions.toggleFilePublic(file)}
                    className="font-medium text-slate-600"
                  >
                    {file.is_public ? "Everyone" : "Private"}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {(user.isAdmin ||
        user.isSuperUser ||
        user.isAccountManager ||
        user.isCustomer) && (
        <div className="border-t border-slate-100 p-3">
          <SmallButton
            onClick={actions.openFileUpload}
            className="w-full py-1.5 text-xs"
          >
            Add documents
          </SmallButton>
        </div>
      )}
    </Card>
  );
}

export default function JobDetailsOverview({
  jobId,
  job,
  user,
  steps,
  tailDetailsFound,
  tailDetails,
  priceBreakdown,
  priceBreakdownComparison,
  invoiceDetails,
  formOptions = {},
  callSign = "",
  vendor,
  flights,
  serviceActivities,
  isAdmin,
  isVendorAccepted,
  actions,
  isCompletedReview = false,
  reviewActions,
  reviewMenu,
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [keyLocationOpen, setKeyLocationOpen] = useState(false);
  const [recordDrawer, setRecordDrawer] = useState(null);
  const [editDrawer, setEditDrawer] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const status = STATUS[job.status] || {
    label: job.status || "Unknown",
    tone: "slate",
  };
  const statusLabel = user.isCustomer && job.status === "A"
    ? "Confirmed"
    : status.label;
  const isInternal =
    user.isAdmin ||
    user.isSuperUser ||
    user.isAccountManager ||
    user.isInternalCoordinator;
  const isVendorUser = !isInternal && Boolean(
    user.isProjectManager || user.isExternalProjectManager,
  );
  const useCompactExternalHeader = user.isCustomer || isVendorUser;
  const canEditOperationalRecords =
    user.isAdmin || user.isSuperUser || user.isAccountManager;
  const canEditReviewJob = user.isAdmin || user.isSuperUser || user.isAccountManager ||
    (user.isInternalCoordinator && job.status !== "I");
  const canEditJob =
    (isCompletedReview ? canEditReviewJob : isInternal) ||
    (user.isCustomer && (job.status === "A" || job.status === "U"));
  const canEditInvoice =
    user.isAdmin || user.isSuperUser || user.isAccountManager;
  const showCustomerIdentity = !isVendorUser;
  const keyLocation = getKeyLocationContent(tailDetails);
  const prioritizeDocuments = ["C", "I", "N"].includes(job.status);
  const showInvoiceDetails = Boolean(isInternal && invoiceDetails);
  const attachmentsCard = (
    <AttachmentsCard
      job={job}
      user={user}
      isAdmin={isAdmin}
      actions={actions}
    />
  );
  const invoiceDetailsCard = showInvoiceDetails ? (
    <Card
      title="Invoice details"
      icon={ReceiptTaxIcon}
      action={canEditInvoice && <CardEditButton label="Edit invoice details" onClick={() => setEditDrawer("invoice")} />}
    >
      <dl>
        <InfoRow label="Vendor">
          {invoiceDetails.vendor?.name}
        </InfoRow>
        <InfoRow label="Vendor charge">
          <Price value={invoiceDetails.vendor?.charge} />
        </InfoRow>
        <InfoRow label="Internal cost">
          <Price
            value={invoiceDetails.internal_additional_cost}
          />
        </InfoRow>
        <InfoRow label="Profit">
          <Price
            value={invoiceDetails.subcontractor_profit}
          />
        </InfoRow>
      </dl>
    </Card>
  ) : null;

  const copyShareLink = async () => {
    if (!job.encoded_id) return;
    const url = `https://www.livetakeoff.com/shared/jobs/${job.encoded_id}/`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Clipboard access can be unavailable on non-secure development hosts.
      window.prompt("Copy this job link", url);
    }
  };

  const showMasterPmWorkflowAction =
    !user.isCustomer && isVendorUser && user.isMasterPM &&
    (job.status === "S" || job.status === "W");

  const renderPrimaryAction = () => {
    if (user.isCustomer && job.status === "U" && user.canConfirmJobs) {
      return (
        <PrimaryButton onClick={() => actions.updateJobStatus("A")}>
          <CheckIcon className="h-4 w-4" />
          Approve job
        </PrimaryButton>
      );
    }
    if (!user.isCustomer && job.status === "U") {
      return (
        <PrimaryButton onClick={() => actions.updateJobStatus("A")}>
          Receive job
        </PrimaryButton>
      );
    }
    if (
      !user.isCustomer &&
      (job.status === "A" || (isVendorUser && user.isMasterPM && !showMasterPmWorkflowAction))
    ) {
      return (
        <PrimaryButton onClick={() => navigate(`/jobs/${jobId}/assignments`)}>
          <UserAddIcon className="h-4 w-4" />
          Assign job
        </PrimaryButton>
      );
    }
    if (!user.isCustomer && job.status === "S") {
      if (isVendorUser && user.canAcceptJobs && !isVendorAccepted) {
        return (
          <PrimaryButton onClick={actions.acceptJob}>Accept job</PrimaryButton>
        );
      }
      return (
        <PrimaryButton onClick={actions.openInitialInspection}>
          <PlayIcon className="h-4 w-4" />
          Start job
        </PrimaryButton>
      );
    }
    if (!user.isCustomer && job.status === "W") {
      return (
        <PrimaryButton onClick={actions.openCompleteJob}>
          <CheckCircleIcon className="h-4 w-4" />
          Complete job
        </PrimaryButton>
      );
    }
    if (
      !user.isCustomer &&
      job.status === "C" &&
      user.enableInvoices &&
      (user.isAdmin || user.isSuperUser || user.isAccountManager)
    ) {
      return (
        <PrimaryButton onClick={actions.openInvoice}>Invoice job</PrimaryButton>
      );
    }
    return null;
  };

  const primaryAction = renderPrimaryAction();
  const primaryActionIsAssignJob =
    !user.isCustomer &&
    job.status !== "U" &&
    (job.status === "A" || (isVendorUser && user.isMasterPM && !showMasterPmWorkflowAction));

  return (
    <div className="job-details-redesign min-h-screen w-full bg-[#F4F6F8] pb-10 text-sm leading-5 text-[#18243A]">
      <header className="sticky top-0 z-40 w-full border-y border-slate-200 bg-white shadow-sm">
        <div className="mx-auto w-full max-w-[1500px] px-4 py-4 sm:px-6">
          <div
            data-testid="job-header-layout"
            className={classNames(
              "relative",
              showMasterPmWorkflowAction && "!flex-col sm:!flex-row",
              useCompactExternalHeader
                ? "flex flex-row items-start justify-between gap-2"
                : "flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between",
            )}
          >
            <div
              data-testid="job-header-identity"
              className={classNames(
                "flex min-w-0 items-start gap-3",
                useCompactExternalHeader ? "flex-1" : "order-1",
                !useCompactExternalHeader && primaryAction && "pr-32 lg:pr-0",
              )}
            >
              <button
                type="button"
                onClick={() => isCompletedReview ? navigate("/completed") : navigate(-1)}
                className="mt-1 rounded-full bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200"
                aria-label="Go back"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-slate-500">
                    {isCompletedReview && "Job Review · "}{job.purchase_order || `Job ${jobId}`}
                  </p>
                  <span
                    className={classNames(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
                      STATUS_STYLES[status.tone],
                    )}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {statusLabel}
                  </span>
                </div>
                <h1 className="mt-1 truncate text-2xl font-bold tracking-tight text-slate-950">
                  {job.tailNumber}
                </h1>
                {showCustomerIdentity && (
                  <p className="mt-0.5 text-sm text-slate-600">
                    {job.integration_customer?.name || job.customer?.name}
                  </p>
                )}
              </div>
            </div>

            {isInternal && tailDetailsFound && (
              <div
                data-testid="job-header-key-location"
                className="order-2 lg:order-3 lg:basis-full"
              >
                <button
                  type="button"
                  onClick={() => setKeyLocationOpen(true)}
                  className="flex w-full max-w-[340px] items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left transition hover:bg-amber-100 sm:ml-11"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
                      <KeyIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">
                        Key location
                      </p>
                      <p className="truncate text-sm font-semibold text-amber-950">
                        {keyLocation.location}
                      </p>
                    </div>
                  </div>
                  <span className="flex-none text-xs font-semibold text-amber-800">
                    View details
                  </span>
                </button>
              </div>
            )}

            <div
              data-testid="job-header-actions"
              className={classNames(
                isCompletedReview ? "flex !flex-wrap items-center gap-y-3" : "flex items-center",
                showMasterPmWorkflowAction && "w-full sm:w-auto",
                useCompactExternalHeader
                  ? "ml-auto flex-none flex-nowrap justify-end gap-1 sm:gap-2"
                  : "order-3 w-full min-w-0 flex-nowrap gap-2 lg:order-2 lg:ml-auto lg:w-auto lg:justify-end",
              )}
            >
              {isCompletedReview ? reviewActions : <div
                data-testid="job-header-scroll-actions"
                className={classNames(
                  isInternal
                    ? "flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto pb-1 lg:flex-none lg:overflow-visible lg:pb-0"
                    : "contents",
                )}
              >
                {isInternal && (
                  <SmallButton
                    onClick={copyShareLink}
                    className={
                      copied
                        ? "!border-slate-700 !bg-black !text-white ring-1 ring-inset ring-white/20 hover:!bg-slate-900"
                        : ""
                    }
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <ShareIcon className="h-4 w-4" />
                    )}
                    <span aria-live="polite">
                      {copied ? "Copied to clipboard!" : "Share"}
                    </span>
                  </SmallButton>
                )}
                {isInternal && !primaryActionIsAssignJob && (
                  <SmallButton
                    onClick={() => navigate(`/jobs/${jobId}/assignments`)}
                  >
                    <UserAddIcon className="h-4 w-4" />
                    Assignments
                  </SmallButton>
                )}
                {showMasterPmWorkflowAction && (
                  <SmallButton onClick={() => navigate(`/jobs/${jobId}/assignments`)}>
                    <UserAddIcon className="h-4 w-4" />
                    Assign job
                  </SmallButton>
                )}
              </div>}
              {!isCompletedReview && <Menu
                as="div"
                className="relative shrink-0"
                data-testid="job-more-actions"
              >
                <Menu.Button as={SmallButton} aria-label="More actions">
                  <DotsHorizontalIcon className="h-5 w-5" />
                  <span className="sr-only">More actions</span>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-50 mt-2 w-52 origin-top-right rounded-xl border border-slate-200 bg-white p-1 shadow-xl focus:outline-none">
                    {!isVendorUser && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => setActivityOpen(true)}
                            className={classNames(
                              "block w-full rounded-lg px-3 py-2 text-left text-sm",
                              active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700",
                            )}
                          >
                            Activity
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {isVendorUser && user.isProjectManager && job.status === "S" && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={actions.openReturnJob}
                            className={classNames(
                              "block w-full rounded-lg px-3 py-2 text-left text-sm",
                              active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700",
                            )}
                          >
                            Return job
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    {user.isCustomer &&
                      (job.status === "A" || job.status === "U") && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={actions.openCancelJob}
                              className={classNames(
                                "block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600",
                                active && "bg-red-50",
                              )}
                            >
                              Cancel job
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    {job.status === "C" &&
                      user.enableInvoices &&
                      (user.isAdmin ||
                        user.isSuperUser ||
                        user.isAccountManager) && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={actions.openNotInvoice}
                              className={classNames(
                                "block w-full rounded-lg px-3 py-2 text-left text-sm",
                                active
                                  ? "bg-slate-100 text-slate-900"
                                  : "text-slate-700",
                              )}
                            >
                              Do not invoice
                            </button>
                          )}
                        </Menu.Item>
                      )}
                  </Menu.Items>
                </Transition>
              </Menu>}
              {isCompletedReview && !user.isCustomer && job.status === "C" && user.enableInvoices &&
                (user.isAdmin || user.isSuperUser || user.isAccountManager) && (
                  <SmallButton onClick={actions.openNotInvoice}>Not invoice job</SmallButton>
                )}
              {primaryAction && (
                <div
                  data-testid="job-primary-action"
                  className={classNames(
                    useCompactExternalHeader || isCompletedReview
                      ? "ml-0 sm:ml-1"
                      : "absolute right-0 top-0 shrink-0 lg:static lg:ml-1",
                  )}
                >
                  {primaryAction}
                </div>
              )}
              {isCompletedReview && reviewMenu?.(() => setActivityOpen(true))}
            </div>
          </div>

          {isCompletedReview && !user.isCustomer && (
            <p className="mt-3 text-sm text-slate-500">Ensure all the photos and details are correct before creating a closeout.</p>
          )}

          {tailDetailsFound && !isInternal && (
            <button
              type="button"
              onClick={() => setKeyLocationOpen(true)}
              className="mt-4 flex w-full max-w-[340px] items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left transition hover:bg-amber-100 sm:ml-11"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
                  <KeyIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">
                    Key location
                  </p>
                  <p className="truncate text-sm font-semibold text-amber-950">
                    {keyLocation.location}
                  </p>
                </div>
              </div>
              <span className="flex-none text-xs font-semibold text-amber-800">
                View details
              </span>
            </button>
          )}

          {job.status !== "T" && steps.length > 0 && (
            <ol
              className="mt-4 hidden w-full items-center lg:flex"
              aria-label="Job progress"
            >
              {steps.map((step, index) => (
                <li
                  key={`${step.name}-${index}`}
                  className={classNames(
                    "flex min-w-0 items-center",
                    index === steps.length - 1 ? "flex-none" : "flex-1",
                  )}
                >
                  <div className="flex flex-none items-center gap-2">
                    <span
                      className={classNames(
                        "flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset",
                        step.status === "complete"
                          ? "bg-emerald-500 text-white ring-emerald-500"
                          : step.status === "current"
                            ? "bg-[#E3222A] text-white ring-[#E3222A]"
                            : "bg-white text-slate-400 ring-slate-300",
                      )}
                    >
                      {step.status === "complete" ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span
                      className={classNames(
                        "whitespace-nowrap text-xs font-semibold",
                        step.status === "current"
                          ? "text-[#E3222A]"
                          : step.status === "complete"
                            ? "text-slate-700"
                            : "text-slate-400",
                      )}
                    >
                      {user.isCustomer && step.name === "Received"
                        ? "Confirmed"
                        : step.name}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <span
                      className={classNames(
                        "mx-3 h-px min-w-[18px] flex-1",
                        step.status === "complete"
                          ? "bg-emerald-300"
                          : "bg-slate-200",
                      )}
                      aria-hidden="true"
                    />
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </header>

      <div className="mx-auto w-full max-w-[1500px] px-4 sm:px-6">
        {(job.tags?.length > 0 || isInternal) && (
          <JobTagsEditor
            tags={job.tags || []}
            availableTags={formOptions.tags || []}
            canEdit={isCompletedReview ? canEditReviewJob : isInternal}
            saving={actions.savingJobSection === "tags"}
            onSave={actions.saveTags}
          />
        )}

        {!isCompletedReview && (job.status === "C" || job.status === "I") && (
          <CustomerFeedback
            jobId={job.id}
            feedback_rating={job.feedback_rating}
            noFeedbackSubmittedYet={job.feedback_rating == null}
            currentUser={user}
            feedback_author={job.feedback_author}
          />
        )}

        {job.show_billing_info && (
          <Card
            title="Billing information"
            icon={CreditCardIcon}
            className="mt-5 !border-indigo-700 !bg-indigo-700 text-white"
            headerClassName="!border-white/20"
            titleClassName="!text-white"
            iconWrapperClassName="!bg-white/[0.15] !text-white"
          >
            <div
              className="px-5 py-4 text-sm leading-6 text-white"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  (job.customer?.billingInfo || "").replace(
                    /\r\n|\n/g,
                    "<br />",
                  ),
                ),
              }}
            />
          </Card>
        )}

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <OperationalCard
            job={job}
            onAirportClick={() => setRecordDrawer("airport")}
            onFboClick={() => setRecordDrawer("fbo")}
            canEdit={canEditJob}
            onEdit={() => setEditDrawer("operational")}
          />
          <JobInformationCard
            job={job}
            user={user}
            isInternal={isInternal}
            actions={actions}
            canEdit={canEditJob}
            onEdit={() => setEditDrawer("job-information")}
          />
        </div>

        <div className="mt-5">
          <ServicesCard
            job={job}
            user={user}
            priceBreakdown={priceBreakdown}
            comparison={priceBreakdownComparison}
            onAddService={actions.openAddService}
            onAddRetainerService={actions.openAddRetainerService}
            onRemoveService={actions.removeService}
            onRemoveRetainerService={actions.removeRetainerService}
            onCompleteService={actions.completeService}
            onCompleteRetainerService={actions.completeRetainerService}
            onSavePricingRow={isCompletedReview && !canEditReviewJob ? undefined : actions.savePricingRow}
          />
        </div>

        {prioritizeDocuments && (
          <div
            data-testid="job-priority-documents"
            className={classNames("mt-5 grid items-start gap-5", showInvoiceDetails && "md:grid-cols-2")}
          >
            {attachmentsCard}
            {invoiceDetailsCard}
          </div>
        )}

        <div className="mt-5 grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <main className="contents min-w-0 lg:block lg:space-y-5">
            <JobComments
              embedded
              initialTab={isCompletedReview ? "photos" : "notes"}
              photosCount={job.total_photos || 0}
              jobStatus={job.status}
            />

            {flights.length > 0 && (
              <InboundFlightCard job={job} flights={flights} />
            )}

            {serviceActivities.length > 0 && (
              <Card
                title="Tail history"
                className="order-last lg:order-none"
                subtitle="One row per service from previous jobs"
                icon={ClockIcon}
                action={
                  serviceActivities.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setShowAllHistory((value) => !value)}
                      className={classNames(
                        "text-xs font-semibold",
                        BRAND_ACTION_CLASS,
                      )}
                    >
                      {showAllHistory ? "Show less" : "View all"}
                    </button>
                  )
                }
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Date</th>
                        <th className="px-5 py-3 font-semibold">P.O.</th>
                        <th className="px-5 py-3 font-semibold">Airport</th>
                        <th className="px-5 py-3 font-semibold">FBO</th>
                        <th className="px-5 py-3 font-semibold">Service</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {serviceActivities
                        .slice(0, showAllHistory ? serviceActivities.length : 3)
                        .map((activity) => (
                          <tr key={activity.id}>
                            <td className="whitespace-nowrap px-5 py-3 text-slate-500">
                              {activity.timestamp}
                            </td>
                            <td className="whitespace-nowrap px-5 py-3">
                              <Link
                                to={`/create-job/review/${activity.job_id}`}
                                className={classNames(
                                  "font-semibold",
                                  LINK_CLASS,
                                )}
                              >
                                {activity.purchase_order}
                              </Link>
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {activity.airport_name}
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {activity.fbo_name || "—"}
                            </td>
                            <td className="whitespace-nowrap px-5 py-3 font-medium text-slate-900">
                              {activity.service_name}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </main>

          <aside className="space-y-5">
            {isCompletedReview && (job.status === "C" || job.status === "I") && (
              <CustomerFeedback
                review
                jobId={job.id}
                feedback_rating={job.feedback_rating}
                noFeedbackSubmittedYet={job.feedback_rating == null}
                currentUser={user}
                feedback_author={job.feedback_author}
              />
            )}
            {isInternal && (
              <AssignmentCard
                jobId={jobId}
                job={job}
                vendor={vendor}
                comparison={priceBreakdownComparison}
                actions={actions}
                canManage={isInternal}
                canEdit={isCompletedReview ? canEditReviewJob : isInternal}
                onVendorClick={() => setRecordDrawer("vendor")}
                onEditLabor={() => setEditDrawer("labor")}
              />
            )}
            {!prioritizeDocuments && attachmentsCard}
            {!prioritizeDocuments && invoiceDetailsCard}
            {isInternal && (
              <Card
                title="Followers"
                icon={MailIcon}
                action={(!isCompletedReview || canEditReviewJob) && <CardEditButton label="Edit followers" onClick={() => setEditDrawer("followers")} />}
              >
                {job.follower_emails?.length > 0 ? (
                  <ul className="divide-y divide-slate-100">
                    {job.follower_emails.map((follower) => (
                    <li
                      key={follower.id}
                      className="px-5 py-3 text-sm text-slate-700"
                    >
                      {follower.email}
                    </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 py-4 text-sm text-slate-500">No followers</p>
                )}
              </Card>
            )}
          </aside>
        </div>
      </div>

      <KeyLocationDrawer
        open={keyLocationOpen}
        onClose={() => setKeyLocationOpen(false)}
        tailDetails={tailDetails}
      />
      <RecordDetailsDrawer
        type="airport"
        open={recordDrawer === "airport"}
        onClose={() => setRecordDrawer(null)}
        record={job.airport}
        canEdit={canEditOperationalRecords}
        saving={actions.savingAirportDetails}
        onSave={actions.saveAirportDetails}
      />
      <RecordDetailsDrawer
        type="fbo"
        open={recordDrawer === "fbo"}
        onClose={() => setRecordDrawer(null)}
        record={job.fbo}
        canEdit={canEditOperationalRecords}
        saving={actions.savingFboDetails}
        onSave={actions.saveFboDetails}
      />
      <RecordDetailsDrawer
        type="vendor"
        open={recordDrawer === "vendor"}
        onClose={() => setRecordDrawer(null)}
        record={vendor}
        canEdit={canEditOperationalRecords}
        saving={actions.savingVendorDetails}
        onSave={actions.saveVendorDetails}
      />
      <JobActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        jobId={jobId}
        currentUser={user}
      />
      <OperationalEditDrawer
        open={editDrawer === "operational"}
        onClose={() => setEditDrawer(null)}
        job={job}
        saving={actions.savingJobSection === "operational"}
        onSave={actions.saveOperationalDetails}
        searchAirports={actions.searchAirports}
        searchFbos={actions.searchFbos}
      />
      <JobInformationEditDrawer
        open={editDrawer === "job-information"}
        onClose={() => setEditDrawer(null)}
        job={job}
        callSign={callSign}
        canEditInternalFields={isInternal}
        canEditCallSign={canEditInvoice}
        canEditPrice={isInternal && user.canSeePrice}
        saving={actions.savingJobSection === "job-information"}
        onSave={actions.saveJobInformation}
        searchAircraftTypes={actions.searchAircraftTypes}
        searchCustomers={actions.searchCustomers}
      />
      <FollowersEditDrawer
        open={editDrawer === "followers"}
        onClose={() => setEditDrawer(null)}
        followers={job.follower_emails || []}
        customerId={job.customer?.id}
        loadCustomerFollowerEmails={actions.loadCustomerFollowerEmails}
        saving={actions.savingJobSection === "followers"}
        onSave={actions.saveFollowers}
      />
      <InvoiceEditDrawer
        open={editDrawer === "invoice"}
        onClose={() => setEditDrawer(null)}
        invoice={invoiceDetails}
        saving={actions.savingJobSection === "invoice"}
        onSave={actions.saveInvoiceDetails}
        searchVendors={actions.searchVendors}
      />
      <LaborEditDrawer
        open={editDrawer === "labor"}
        onClose={() => setEditDrawer(null)}
        job={job}
        saving={actions.savingJobSection === "labor"}
        onSave={actions.saveLaborDetails}
      />
    </div>
  );
}
